import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { Inbox, Loader2, MessageSquare, Phone, Mail, ChevronRight,
         CheckCircle2, X, UserCheck, Clock } from 'lucide-react';
import { toast } from 'sonner';
import ReseauLayout from '../../components/reseau/ReseauLayout';
import useCockpitAction from '../../lib/useCockpitAction';

const STATUS_LABEL = {
    new: { label: 'Nouvelle', color: 'bg-amber-100 text-amber-800 border-amber-200' },
    assigned: { label: 'Affectée', color: 'bg-sky-100 text-sky-800 border-sky-200' },
    contact_attempted: { label: 'Tentative', color: 'bg-blue-100 text-blue-800 border-blue-200' },
    contacted: { label: 'Contactée', color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
    qualified: { label: 'Qualifiée', color: 'bg-purple-100 text-purple-800 border-purple-200' },
    appointment_scheduled: { label: 'Rendez-vous', color: 'bg-violet-100 text-violet-800 border-violet-200' },
    in_progress: { label: 'En cours', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    converted: { label: 'Convertie', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    not_converted: { label: 'Non convertie', color: 'bg-slate-100 text-slate-700 border-slate-200' },
    invalid: { label: 'Invalide', color: 'bg-slate-100 text-slate-500 border-slate-200' },
    spam: { label: 'Spam', color: 'bg-rose-100 text-rose-800 border-rose-200' },
    archived: { label: 'Archivée', color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const INTENT_TYPE_FR = {
    callback: 'Demande de rappel',
    estimate: 'Demande d\'estimation',
    question: 'Question',
    present_project: 'Présentation d\'un projet',
    search_agent: 'Recherche d\'un agent',
    appointment: 'Demande de rendez-vous',
    contact_agency: 'Contact agence',
};

const GROUPS = [
    { key: 'new', label: 'Nouvelles' },
    { key: 'to_process', label: 'À traiter' },
    { key: 'in_progress', label: 'En cours' },
    { key: 'converted', label: 'Converties' },
    { key: 'closed', label: 'Clôturées' },
];

const GROUP_TO_STATUSES = {
    new: ['new'],
    to_process: ['assigned', 'contact_attempted'],
    in_progress: ['contacted', 'qualified', 'appointment_scheduled', 'in_progress'],
    converted: ['converted'],
    closed: ['not_converted', 'invalid', 'spam', 'archived'],
};

function IntentDetailModal({ intentId, onClose, onChanged }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [note, setNote] = useState('');
    const runAction = useCockpitAction();

    useEffect(() => {
        if (!intentId) return;
        setLoading(true);
        api.get(`/seller-intents/${intentId}`)
            .then(r => setData(r.data))
            .catch(() => toast.error('Accès refusé ou demande introuvable'))
            .finally(() => setLoading(false));
    }, [intentId]);

    if (!intentId) return null;

    /**
     * `update(body)` — Patch optimiste sur la projection locale + rollback exact en 5xx.
     * Sur succès, on resynchronise via GET (source de vérité serveur).
     * Sur échec, l'état local est restauré exactement.
     */
    const update = async (body) => {
        const snapshot = data;
        const optimisticNext = { ...(data || {}), ...body };
        return await runAction({
            key: `intent-update-${intentId}-${Object.keys(body).join('+')}`,
            optimistic: () => setData(optimisticNext),
            rollback:   () => setData(snapshot),
            call:       () => api.patch(`/seller-intents/${intentId}`, body),
            onSuccess:  async () => {
                // Toast contextualisé selon le champ modifié
                if ('status' in body) {
                    const STATUS_LBL = {
                        new: 'Nouvelle',
                        assigned: 'Affectée',
                        contact_attempted: 'Contact tenté',
                        contacted: 'Contactée',
                        qualified: 'Demande qualifiée',
                        appointment_scheduled: 'Rendez-vous programmé',
                        in_progress: 'Accompagnement engagé',
                        converted: 'Demande convertie',
                        not_converted: 'Demande clôturée',
                        invalid: 'Marquée invalide',
                        spam: 'Marquée comme spam',
                        archived: 'Archivée',
                    };
                    toast.success(`Statut mis à jour : ${STATUS_LBL[body.status] || body.status}.`);
                } else if ('note' in body) {
                    toast.success('Note privée enregistrée. Visible par vous et votre équipe uniquement.');
                } else if ('qualification' in body) {
                    toast.success('Qualification enregistrée.');
                } else {
                    toast.success('Demande vendeur mise à jour.');
                }
                try {
                    const r = await api.get(`/seller-intents/${intentId}`);
                    setData(r.data);
                } catch { /* keep optimistic */ }
                onChanged?.();
            },
            errorLabel: "La mise à jour n'a pas été enregistrée.",
        });
    };

    /**
     * Note privée : conserve le texte saisi tant que le serveur n'a pas confirmé.
     * Si l'enregistrement échoue (5xx), le draft reste dans le champ pour
     * permettre une nouvelle tentative.
     */
    const submitNote = async () => {
        if (!note.trim()) return;
        const draft = note;
        const ok = await update({ note: draft });
        if (ok) {
            setNote('');
        }
        // si échec : le texte reste, l'utilisateur peut réessayer
    };

    const attempt = async () => {
        const snapshot = data;
        const optimisticNext = { ...(data || {}), status: 'contact_attempted' };
        await runAction({
            key: `intent-attempt-${intentId}`,
            optimistic: () => setData(optimisticNext),
            rollback:   () => setData(snapshot),
            call:       () => api.post(`/seller-intents/${intentId}/contact-attempted`),
            onSuccess:  async () => {
                toast.success('Tentative de contact enregistrée. Pensez à programmer un rappel si nécessaire.');
                try {
                    const r = await api.get(`/seller-intents/${intentId}`);
                    setData(r.data);
                } catch { /* keep optimistic */ }
                onChanged?.();
            },
            errorLabel: "La tentative n'a pas été enregistrée.",
        });
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
             data-testid="intent-detail-modal" onClick={onClose}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                 onClick={e => e.stopPropagation()}>
                <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
                    <h2 className="font-bold text-slate-900">Demande vendeur</h2>
                    <button onClick={onClose} data-testid="intent-detail-close">
                        <X className="w-5 h-5 text-slate-400" />
                    </button>
                </div>
                {loading || !data ? (
                    <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#681793] mx-auto" /></div>
                ) : (
                    <div className="p-5 space-y-4">
                        {/* En-tête statut */}
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <span className={`px-2 py-1 rounded border text-xs ${STATUS_LABEL[data.status]?.color || ''}`}>
                                {STATUS_LABEL[data.status]?.label}
                            </span>
                            <span className="text-xs text-slate-500">
                                Reçue le {new Date(data.created_at).toLocaleDateString('fr-FR')}
                            </span>
                        </div>

                        {/* Données vendeur — section autorisée */}
                        <section className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                            <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-2">Données du vendeur</p>
                            <p className="text-sm font-semibold text-slate-900">
                                {data.seller_first_name} {data.seller_last_name_initial && `${data.seller_last_name_initial}.`}
                            </p>
                            <p className="text-sm text-slate-700">
                                <Mail className="inline w-4 h-4 mr-1" /> {data.seller_email || '—'}
                            </p>
                            {data.seller_phone && (
                                <p className="text-sm text-slate-700">
                                    <Phone className="inline w-4 h-4 mr-1" /> {data.seller_phone}
                                </p>
                            )}
                            <p className="text-xs text-slate-500 mt-2">
                                Préférence : {data.contact_preference || 'e-mail'}
                            </p>
                        </section>

                        {/* Besoin */}
                        <section>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-2">Besoin exprimé</p>
                            <p className="text-sm text-slate-900 font-medium">{INTENT_TYPE_FR[data.intent_type] || data.intent_type}</p>
                            {data.territory_label && (
                                <p className="text-xs text-slate-600 mt-1">Zone : {data.territory_label}</p>
                            )}
                            {data.property_type && (
                                <p className="text-xs text-slate-600">Bien : {data.property_type}</p>
                            )}
                            {data.project_timeline && (
                                <p className="text-xs text-slate-600">Horizon : {data.project_timeline}</p>
                            )}
                            {data.message && (
                                <p className="text-sm text-slate-700 mt-2 italic">« {data.message} »</p>
                            )}
                        </section>

                        {/* Parcours observé (consenti uniquement) */}
                        {data.context_summary ? (
                            <section className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                                <p className="text-[10px] text-purple-700 uppercase tracking-wide mb-1">Parcours observé (avec consentement)</p>
                                <p className="text-sm text-slate-700">{data.context_summary}</p>
                                <p className="text-[10px] text-slate-500 mt-2 italic">
                                    Aucun historique détaillé de navigation n&apos;a été transmis.
                                </p>
                            </section>
                        ) : (
                            <section className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                                <p className="text-xs text-slate-500">
                                    Le vendeur n&apos;a pas consenti au partage du contexte synthétique.
                                </p>
                            </section>
                        )}

                        {/* Actions */}
                        <section className="border-t pt-4 space-y-2">
                            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Actions</p>
                            <div className="flex flex-wrap gap-2">
                                <button onClick={attempt}
                                        className="bg-[#681793] text-white text-sm px-3 py-2 rounded-lg flex items-center gap-1"
                                        data-testid="btn-contact-attempted">
                                    <Phone className="w-4 h-4" /> Tentative effectuée
                                </button>
                                <button onClick={() => update({ status: 'contacted' })}
                                        className="bg-sky-600 text-white text-sm px-3 py-2 rounded-lg flex items-center gap-1"
                                        data-testid="btn-mark-contacted">
                                    <CheckCircle2 className="w-4 h-4" /> Marquer contacté
                                </button>
                                <button onClick={() => update({ status: 'appointment_scheduled' })}
                                        className="bg-violet-600 text-white text-sm px-3 py-2 rounded-lg flex items-center gap-1"
                                        data-testid="btn-appointment">
                                    <UserCheck className="w-4 h-4" /> Rendez-vous
                                </button>
                                <button onClick={() => update({ status: 'converted', conversion_status: 'qualified_project' })}
                                        className="bg-emerald-600 text-white text-sm px-3 py-2 rounded-lg flex items-center gap-1"
                                        data-testid="btn-converted">
                                    Convertie
                                </button>
                                <button onClick={() => update({ status: 'not_converted', loss_reason: 'no_response' })}
                                        className="bg-slate-500 text-white text-sm px-3 py-2 rounded-lg"
                                        data-testid="btn-not-converted">
                                    Non convertie
                                </button>
                            </div>
                        </section>

                        {/* Qualification */}
                        <section className="border-t pt-4 flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-slate-500">Qualification :</span>
                            {['confirmed_project', 'to_mature', 'info_request', 'out_of_zone'].map(q => (
                                <button key={q}
                                        onClick={() => update({ qualification: q })}
                                        className={`text-xs px-2 py-1 rounded border ${
                                            data.qualification === q
                                                ? 'bg-[#681793] text-white border-[#681793]'
                                                : 'bg-white text-slate-700 border-slate-200'
                                        }`}
                                        data-testid={`btn-qualify-${q}`}>
                                    {q.replace(/_/g, ' ')}
                                </button>
                            ))}
                        </section>

                        {/* Notes internes */}
                        <section className="border-t pt-4">
                            <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-2">Notes internes</p>
                            <textarea value={note} onChange={e => setNote(e.target.value)}
                                      rows={2} maxLength={2000}
                                      className="w-full p-2 border border-slate-200 rounded-lg text-sm"
                                      placeholder="Ajouter une note privée…"
                                      data-testid="note-input" />
                            <button onClick={submitNote}
                                    disabled={!note.trim()}
                                    className="bg-slate-700 text-white text-sm px-3 py-1.5 rounded-lg mt-2 disabled:opacity-50"
                                    data-testid="btn-add-note">
                                Ajouter
                            </button>
                            <p className="text-[10px] text-slate-400 mt-1 italic">
                                En cas d&apos;échec, votre texte reste dans le champ pour réessayer.
                            </p>
                        </section>

                        <p className="text-[10px] text-slate-400 italic border-t pt-2">
                            Reçue depuis {data.source_label}.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

function IntentRow({ intent, onOpen }) {
    const st = STATUS_LABEL[intent.status] || STATUS_LABEL.new;
    const delaySec = intent.created_at
        ? Math.floor((Date.now() - new Date(intent.created_at).getTime()) / 1000)
        : 0;
    const delayLabel = delaySec < 3600 ? `${Math.floor(delaySec / 60)} min`
        : delaySec < 86400 ? `${Math.floor(delaySec / 3600)}h`
        : `${Math.floor(delaySec / 86400)}j`;
    return (
        <button onClick={() => onOpen(intent.id)}
                data-testid={`intent-row-${intent.id}`}
                className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-[#681793]/40 transition flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`px-2 py-0.5 rounded border text-xs ${st.color}`}>
                        {st.label}
                    </span>
                    <span className="text-xs text-slate-500">
                        <Clock className="inline w-3 h-3" /> il y a {delayLabel}
                    </span>
                </div>
                <p className="text-sm font-semibold text-slate-900">
                    {intent.seller_first_name} {intent.seller_last_name_initial && `${intent.seller_last_name_initial}.`}
                    <span className="text-slate-500 font-normal ml-2">
                        — {INTENT_TYPE_FR[intent.intent_type] || intent.intent_type}
                    </span>
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                    {intent.territory_label || 'Zone non précisée'} · depuis {intent.source_label}
                </p>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
        </button>
    );
}

export default function ReseauSellerIntentsPage() {
    const [items, setItems] = useState([]);
    const [groups, setGroups] = useState({});
    const [kpi, setKpi] = useState(null);
    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState('new');
    const [openIntent, setOpenIntent] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [list, k] = await Promise.all([
                api.get('/seller-intents'),
                api.get('/seller-intents/kpi'),
            ]);
            setItems(list.data.items || []);
            setGroups(list.data.groups || {});
            setKpi(k.data);
        } catch (e) {
            toast.error('Impossible de charger les demandes');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const allowedStatuses = GROUP_TO_STATUSES[group] || [];
    const filtered = items.filter(i => allowedStatuses.includes(i.status));

    return (
        <ReseauLayout headerTitle="Demandes vendeurs" headerSubtitle="Les intentions exprimées par les vendeurs depuis votre profil public et votre synthèse de confiance.">
            <main className="mx-auto px-4 sm:px-6 py-6 space-y-5" style={{ maxWidth: 'var(--reseau-maxw-page)' }}>

                {/* KPI */}
                {kpi && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="kpi-grid">
                        <div className="reseau-card p-4 text-center">
                            <p className="text-2xl font-bold" style={{ color: 'var(--reseau-ink)' }}>{kpi.total}</p>
                            <p className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>Total demandes</p>
                        </div>
                        <div className="reseau-card p-4 text-center">
                            <p className="text-2xl font-bold" style={{ color: 'var(--reseau-warning)' }}>{kpi.new}</p>
                            <p className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>Nouvelles</p>
                        </div>
                        <div className="reseau-card p-4 text-center">
                            <p className="text-2xl font-bold" style={{ color: 'var(--reseau-success)' }}>{kpi.converted}</p>
                            <p className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>
                                Converties{kpi.total > 0 && ` (${Math.round(kpi.conversion_rate * 100)}%)`}
                            </p>
                        </div>
                        <div className="reseau-card p-4 text-center">
                            <p className="text-2xl font-bold" style={{ color: 'var(--reseau-ink)' }}>
                                {kpi.avg_first_response_hours != null ? `${kpi.avg_first_response_hours.toFixed(1)}h` : '—'}
                            </p>
                            <p className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>Délai 1re action</p>
                        </div>
                    </div>
                )}

                {/* Onglets de groupe */}
                <div className="reseau-card p-2">
                    <div className="flex gap-1 overflow-x-auto" role="tablist">
                        {GROUPS.map(g => (
                            <button key={g.key}
                                    onClick={() => setGroup(g.key)}
                                    role="tab" aria-selected={group === g.key}
                                    data-testid={`group-${g.key}`}
                                    className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition font-medium ${
                                        group === g.key ? 'reseau-btn-primary' : ''
                                    }`}
                                    style={group === g.key ? {
                                        background: 'var(--reseau-violet)', color: '#fff'
                                    } : {
                                        color: 'var(--reseau-ink-soft)'
                                    }}>
                                {g.label} ({groups[g.key] || 0})
                            </button>
                        ))}
                    </div>
                </div>

                {/* Liste */}
                {loading ? (
                    <div className="text-center py-10">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--reseau-violet)' }} />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="reseau-empty" data-testid="empty-group">
                        <Inbox className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--reseau-ink-faint)' }} />
                        <h3>Vos prochaines demandes apparaîtront ici</h3>
                        <p>
                            Vérifiez que votre profil public présente clairement vos zones d&apos;intervention et votre spécialité pour
                            recevoir des demandes pertinentes.
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2 justify-center">
                            <Link to="/reseau/cockpit"
                                  className="reseau-btn reseau-btn-secondary"
                                  data-testid="empty-intents-cockpit">
                                Voir mes priorités
                            </Link>
                            <Link to="/profile"
                                  className="reseau-btn reseau-btn-primary"
                                  data-testid="empty-intents-profile">
                                Compléter mon profil
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2" data-testid="intents-list">
                        {filtered.map(it => (
                            <IntentRow key={it.id} intent={it}
                                       onOpen={(id) => setOpenIntent(id)} />
                        ))}
                    </div>
                )}
            </main>

            <IntentDetailModal intentId={openIntent}
                                onClose={() => setOpenIntent(null)}
                                onChanged={load} />
        </ReseauLayout>
    );
}
